import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, InputNumber, Modal, Space, Tag, message } from 'antd';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from '../../config';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Réservations d'une paire, vues depuis la fiche article — branchées sur le
// MODULE Réservations (mêmes gestes, mêmes garde-fous que l'écran dédié) :
//  - réservation cliente active → « Désactiver » (motif obligatoire, tracé) ;
//  - paire engagée par une commande ET encore en rayon (AFFECTEE) → « Libérer »
//    (admin, motif obligatoire) ; chez le livreur → on renvoie vers le hub.
const ReservationPanel = ({ variantId, onChange }) => {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [clientes, setClientes] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [qte, setQte] = useState(1);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null);   // { type: 'desactiver'|'liberer', cible, motif }

  const load = useCallback(async () => {
    if (!variantId) return;
    try {
      const { data } = await axios.get(`${DOMAIN}/api/reservations`, {
        params: { id_varianteProduit: variantId },
      });
      setClientes(data.clientes || []);
      setCommandes(data.commandes || []);
    } catch (e) {
      // panneau non bloquant (rôle non autorisé → on n'affiche rien)
    }
  }, [variantId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    await load();
    if (onChange) onChange();
  };

  const reserver = async () => {
    if (!qte || qte < 1) { message.warning('Quantité ≥ 1.'); return; }
    setLoading(true);
    try {
      await axios.post(`${DOMAIN}/api/catalogue/variante/${variantId}/reservation`, { qte });
      message.success('Paire réservée.');
      setQte(1);
      await refresh();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Réservation échouée.');
    } finally {
      setLoading(false);
    }
  };

  const executerAction = async () => {
    if (!action?.motif?.trim()) { message.warning('Le motif est obligatoire.'); return; }
    setLoading(true);
    try {
      if (action.type === 'desactiver') {
        await axios.put(`${DOMAIN}/api/reservations/${action.cible.id_reservation}/desactiver`, { motif: action.motif });
        message.success('Réservation désactivée — la paire redevient disponible.');
      } else {
        await axios.put(`${DOMAIN}/api/reservations/commande/${action.cible.id_detail_livraison}/liberer`, { motif: action.motif });
        message.success('Paire libérée — elle redevient disponible.');
      }
      setAction(null);
      await refresh();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Action impossible.');
    } finally {
      setLoading(false);
    }
  };

  const clienteActive = (r) => r.statut === 'active' && Number(r.expiree_effective) !== 1;
  const badgeCliente = (r) => {
    if (clienteActive(r)) return <Tag color="gold">réservée</Tag>;
    if (r.statut === 'convertie') return <Tag color="green">convertie</Tag>;
    return <Tag>{r.statut === 'liberee' ? 'désactivée' : 'expirée'}</Tag>;
  };

  return (
    <div style={{ borderTop: '1px solid #eee8de', marginTop: 16, paddingTop: 12 }}>
      <div style={{ fontWeight: 600, color: '#071c3d', marginBottom: 8 }}>Réservation</div>
      <Space>
        <InputNumber inputMode="decimal" min={1} value={qte} onChange={setQte} style={{ width: 90 }} />
        <Button type="primary" loading={loading} onClick={reserver} style={{ background: '#071c3d', borderColor: '#071c3d' }}>
          Réserver
        </Button>
      </Space>

      {clientes.filter(clienteActive).length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {clientes.filter(clienteActive).map((r) => (
            <div key={r.id_reservation} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {badgeCliente(r)}
              <span>{r.qte} paire{r.qte > 1 ? 's' : ''}{r.cliente ? ` · ${r.cliente}` : ''}</span>
              {r.expire_le && <span style={{ color: '#8a93a3' }}>· expire le {r.expire_le}</span>}
              <Button size="small" danger onClick={() => setAction({ type: 'desactiver', cible: r, motif: '' })}>
                Désactiver
              </Button>
            </div>
          ))}
        </div>
      )}

      {commandes.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#8a93a3' }}>Engagées dans une commande :</span>
          {commandes.map((r) => {
            const enRayon = (r.statut || 'AFFECTEE') === 'AFFECTEE';
            return (
              <div key={r.id_detail_livraison} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Tag color={enRayon ? 'blue' : 'orange'}>{enRayon ? 'affectée (en rayon)' : 'chez le livreur'}</Tag>
                <span>{r.qte} paire{r.qte > 1 ? 's' : ''} · #{r.id_commande} {r.cliente ? `· ${r.cliente}` : ''}</span>
                {isAdmin && enRayon && (
                  <Button size="small" danger onClick={() => setAction({ type: 'liberer', cible: r, motif: '' })}>
                    Libérer
                  </Button>
                )}
                {!enRayon && <span style={{ fontSize: 12, color: '#8a93a3' }}>· se clôture dans le hub livraisons</span>}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={!!action}
        onCancel={() => setAction(null)}
        onOk={executerAction}
        okText={action?.type === 'desactiver' ? 'Désactiver' : 'Libérer'}
        okButtonProps={{ danger: true, loading }}
        cancelText="Annuler"
        title={action?.type === 'desactiver' ? 'Désactiver la réservation' : 'Libérer la paire engagée'}
        centered
      >
        <p style={{ color: '#8a93a3' }}>
          {action?.type === 'desactiver'
            ? 'La paire redevient disponible immédiatement. Le geste est tracé.'
            : 'La paire est détachée de sa commande et redevient disponible. Le geste est tracé ; la commande reste à traiter.'}
        </p>
        <Input.TextArea
          rows={2}
          maxLength={200}
          value={action?.motif || ''}
          onChange={(e) => setAction((a) => a && { ...a, motif: e.target.value })}
          placeholder="Motif (obligatoire)"
        />
      </Modal>
    </div>
  );
};

export default ReservationPanel;
